import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Server, Boxes, Cpu, MemoryStick, HardDrive } from 'lucide-react';
import { Container } from '@vnetwork/ui/layout';
import { DetailHeader } from '@/components/detail-header';
import { Tabs, TabsList, TabsTrigger, TabsContent, Skeleton, Chip } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { useZoneCapacity } from '@/api/capacities-cloud';
import { formatGb } from '../dashboard/capacity-format';
import { cloudStatusView } from './cloud-format';
import { hostColumns, datastoreColumns, instanceColumns, asPct } from './zone-column-definitions';
import { CloudTableSection } from './cloud-table-section';
import { StatCardGrid, type StatCardItem } from './stat-card-grid';

export interface CloudZoneDetailPageProps {
  /** Zone id. Falls back to the `:id` route param when omitted. */
  zoneId?: string;
}

/**
 * Cloud Zone detail — @ui detail pattern: Breadcrumbs + title row, a `StatCardGrid`
 * header (aggregate CPU/memory/storage), then Hosts / Datastores / Instances tabs
 * (each a searchable table). Hosts, datastores and instances are all embedded in
 * the single `/capacities/zone/:id` response, so one query feeds every tab.
 * Read-only.
 */
export default function CloudZoneDetailPage({ zoneId: idProp }: CloudZoneDetailPageProps) {
  const params = useParams();
  const zoneId = idProp ?? params.id ?? '';
  const [tab, setTab] = useState('hosts');

  const cap = useZoneCapacity(zoneId);
  const zone = cap.data;
  const compute = zone?.compute;
  const storage = zone?.storage;
  const status = cloudStatusView(zone?.status);
  const zoneName = zone?.zone_name || zoneId;

  const hosts = compute?.hosts ?? [];
  const datastores = storage?.datastores ?? [];
  const instances = zone?.instances ?? [];
  const healthyHosts = hosts.filter((h) => cloudStatusView(h.status).tone === 'success').length;

  const statCards: StatCardItem[] = [
    { label: 'Hosts', value: hosts.length, hint: `${healthyHosts} healthy`, icon: Server, color: 'var(--vn-success-solid)' },
    { label: 'VMs', value: instances.length, hint: 'Running', icon: Boxes, color: 'var(--vn-accent-solid)' },
    { label: 'CPU Usage', value: asPct(compute?.avg_cpu_usage_pct), hint: 'Average', icon: Cpu, color: 'var(--vn-warning-solid)' },
    { label: 'Memory Usage', value: asPct(compute?.avg_memory_usage_pct), hint: 'Average', icon: MemoryStick, color: 'var(--vn-danger-solid)' },
    {
      label: 'Storage',
      value: asPct(storage?.usage_pct),
      hint: storage ? `${formatGb(storage.used_gb)} / ${formatGb(storage.total_capacity_gb)}` : 'Used',
      icon: HardDrive,
      color: 'var(--vn-info-solid)',
    },
  ];

  const tableState = {
    isLoading: cap.isLoading,
    isError: cap.isError,
    onRetry: () => cap.refetch(),
  };

  return (
    <Container size="full">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <DetailHeader
          parent={[
            { label: 'Capacities & Resources', to: '/capacities' },
            { label: 'Cloud Resources', to: '/capacities?tab=cloud' },
          ]}
          current={zoneName}
          title={zoneName}
          meta={
            <>
              <StatusPill tone={status.tone}>{status.label}</StatusPill>
              {zone?.provider_name ? <Chip tone="neutral">{zone.provider_name}</Chip> : null}
            </>
          }
        />

        {cap.isLoading ? <Skeleton style={{ height: 96 }} /> : compute ? <StatCardGrid items={statCards} /> : null}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="hosts">Hosts</TabsTrigger>
            <TabsTrigger value="datastores">Datastores</TabsTrigger>
            <TabsTrigger value="instances">Instances</TabsTrigger>
          </TabsList>

          <TabsContent value="hosts">
            <CloudTableSection
              columns={hostColumns}
              data={hosts}
              {...tableState}
              getRowId={(r) => r.id}
              searchText={(r) => `${r.name} ${r.status}`}
              searchPlaceholder="Search hosts…"
              emptyTitle="No hosts"
              emptyDescription="This zone reports no hosts."
            />
          </TabsContent>

          <TabsContent value="datastores">
            <CloudTableSection
              columns={datastoreColumns}
              data={datastores}
              {...tableState}
              getRowId={(r) => r.id}
              searchText={(r) => `${r.name} ${r.type}`}
              searchPlaceholder="Search datastores…"
              emptyTitle="No datastores"
              emptyDescription="This zone reports no datastores."
            />
          </TabsContent>

          <TabsContent value="instances">
            <CloudTableSection
              columns={instanceColumns}
              data={instances}
              {...tableState}
              getRowId={(r) => r.uid}
              searchText={(r) => `${r.name} ${r.host_name ?? ''} ${r.status}`}
              searchPlaceholder="Search instances…"
              emptyTitle="No instances"
              emptyDescription="No instances reported for this zone."
            />
          </TabsContent>
        </Tabs>
      </div>
    </Container>
  );
}
